import type { TourDefinition } from '../training/tours';

/**
 * Тур профілю працівника. Ідентифікатор для реєстрації: 'people-profile'.
 * Цілі — атрибути data-tour на сторінці профілю (features/people/pages/ProfilePage.tsx і profile/*).
 */
export const PEOPLE_PROFILE_TOUR_ID = 'people-profile';

export const PEOPLE_PROFILE_TOUR: Omit<TourDefinition, 'id'> = {
  title: 'Профіль працівника',
  summary: 'Кадрові реквізити, посади й грейди, особисті дані, посадова папка з ознайомленням, навчання й допуски.',
  missingHint: 'Відкрийте профіль працівника в розділі «Персонал» — підказки прив’яжуться до його частин.',
  steps: [
    {
      id: 'header',
      target: 'people-profile-header',
      title: 'Шапка профілю',
      body: 'Посада, підрозділ і стан працівника. Бейджі показують випробувальний строк і рішення про допуск, а бурштиновий блок — те, що потребує рішення (наприклад, строк випробування минув).',
      placement: 'bottom',
    },
    {
      id: 'tabs',
      target: 'people-profile-tabs',
      title: 'Розділи профілю',
      body: 'Загальне, посади й грейди, особисті дані, посадова папка, навчання й допуски. Особисті дані бачать лише сам працівник і HR — інший перегляд пишеться в журнал аудиту.',
    },
    {
      id: 'general',
      target: 'people-profile-general',
      title: 'Кадрові реквізити',
      body: 'Форма відносин, дата прийняття, графік і робочі контакти. Змінює їх HR; для трудового договору строк випробування понад 3 місяці система позначає як такий, що потребує правової оцінки.',
    },
    {
      id: 'folder',
      target: 'people-profile-tab-folder',
      title: 'Посадова папка й ознайомлення',
      body: 'Документи посади зі станом ознайомлення кожного: чинна версія, прострочений строк, остання інспекція знань. Нову версію регламенту працівник підтверджує повторно.',
    },
    {
      id: 'links',
      target: 'people-profile-links',
      title: 'Пов’язане',
      body: 'Швидкий перехід до адаптації, записів реєстру порушень, оцінок відповідності посаді й бонусів працівника. HR звідси починає завершення співпраці чи переведення.',
      placement: 'left',
    },
  ],
};

/**
 * Тур процедури завершення співпраці. Ідентифікатор для реєстрації: 'people-offboarding'.
 * Цілі — атрибути data-tour на картці процедури (features/people/pages/OffboardingPage.tsx).
 */
export const PEOPLE_OFFBOARDING_TOUR_ID = 'people-offboarding';

export const PEOPLE_OFFBOARDING_TOUR: Omit<TourDefinition, 'id'> = {
  title: 'Завершення співпраці',
  summary: 'Майстер передачі справ, доступів і майна: чек-лист, передача задач, підтвердження, завершення.',
  missingHint: 'Відкрийте процедуру завершення співпраці в розділі «Персонал» — підказки прив’яжуться до чек-листа.',
  steps: [
    {
      id: 'steps',
      target: 'people-offboarding-steps',
      title: 'Кроки майстра',
      body: 'Спершу працівник і вид процедури, далі чек-лист передачі, наприкінці — завершення. Поки є блокувальні пункти, завершити процедуру не можна.',
      placement: 'bottom',
    },
    {
      id: 'checklist',
      target: 'people-offboarding-checklist',
      title: 'Чек-лист передачі',
      body: 'Система сама рахує відкриті задачі, проєкти, підлеглих, підрозділи, токени й вебхуки працівника. Пункт «блокує» — обов’язковий і ще не виконаний.',
    },
    {
      id: 'transfer',
      target: 'people-offboarding-transfer',
      title: 'Передача задач',
      body: 'Відкриті задачі (зокрема погодження) передаються новому відповідальному одним кроком; авторство задач зберігається.',
    },
    {
      id: 'confirm',
      target: 'people-offboarding-actions',
      title: 'Підтвердження й відхилення',
      body: 'Документи й майно HR підтверджує вручну з приміткою. Блокувальний пункт можна відхилити лише з обґрунтуванням — воно потрапляє в журнал аудиту.',
    },
    {
      id: 'complete',
      target: 'people-offboarding-complete',
      title: 'Завершення процедури',
      body: 'Під час завершення система відкликає токени й вебхуки, деактивує доступ до ЄРС і створює нагадування адміністратору Google.',
      placement: 'top',
    },
  ],
};
